import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { XCircleIcon, AlertTriangleIcon } from 'lucide-react';
interface RejectExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  expense: any;
}
export function RejectExpenseModal({
  isOpen,
  onClose,
  onSubmit,
  expense
}: RejectExpenseModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  // Common rejection reasons
  const commonReasons = ['Missing itemized receipt', 'Exceeds per diem limit without approval', 'Expense type incorrectly categorized', 'Missing required attendee information', 'Duplicate submission', 'Non-business related expense', 'Insufficient documentation', 'Outside of company policy'];
  // Handle reason selection
  const handleReasonSelect = (selectedReason: string) => {
    setReason(selectedReason);
    setError('');
  };
  // Format amount safely
  const formatAmount = (amount: string | number | undefined): string => {
    if (!amount) return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    onSubmit(reason);
    setReason('');
    setError('');
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Reject Expense" size="md" data-id="element-688">
      <div className="p-6" data-id="element-689">
        {/* Expense summary */}
        <div className="mb-6 p-4 bg-gray-750 border border-gray-700 rounded-md" data-id="element-690">
          <div className="flex justify-between items-start" data-id="element-691">
            <div data-id="element-692">
              <h4 className="text-sm font-medium text-gray-300" data-id="element-693">
                Expense Summary
              </h4>
              <p className="text-sm text-gray-400 mt-1" data-id="element-694">
                {expense?.type} {expense?.subtype && `- ${expense.subtype}`}
              </p>
            </div>
            <div className="text-right" data-id="element-695">
              <div className="text-lg font-semibold text-gray-200" data-id="element-696">
                ${formatAmount(expense?.amount)}
              </div>
              <p className="text-xs text-gray-400" data-id="element-697">
                {expense?.date && new Date(expense.date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        {/* Warning */}
        <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-md flex items-start" data-id="element-698">
          <AlertTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-699" />
          <div data-id="element-700">
            <h3 className="text-sm font-medium text-red-300" data-id="element-701">
              Rejection Notice
            </h3>
            <p className="text-sm text-red-200 mt-1" data-id="element-702">
              You are about to reject an expense of $
              {formatAmount(expense?.amount)}. This action will notify the
              employee and require them to resubmit or provide additional
              information.
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit} data-id="element-703">
          {/* Rejection reason */}
          <div className="mb-4" data-id="element-704">
            <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-705">
              Rejection Reason <span className="text-red-500" data-id="element-706">*</span>
            </label>
            <textarea value={reason} onChange={e => {
            setReason(e.target.value);
            setError('');
          }} rows={4} className={`w-full px-3 py-2 bg-gray-700 border ${error ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="Provide a clear reason for rejecting this expense..." data-id="element-707"></textarea>
            {error && <p className="mt-1 text-sm text-red-500" data-id="element-708">{error}</p>}
          </div>
          {/* Common reasons */}
          <div className="mb-6" data-id="element-709">
            <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-710">
              Common Rejection Reasons
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-711">
              {commonReasons.map((commonReason, index) => <button key={index} type="button" onClick={() => handleReasonSelect(commonReason)} className="text-xs bg-gray-700 border border-gray-600 rounded-full px-3 py-1 text-gray-300 hover:bg-gray-600 transition-colors" data-id="element-712">
                  {commonReason}
                </button>)}
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-713">
            <Button type="button" variant="outline" onClick={onClose} data-id="element-714">
              Cancel
            </Button>
            <Button type="submit" variant="destructive" data-id="element-715">
              <XCircleIcon size={16} className="mr-1.5" data-id="element-716" />
              Reject Expense
            </Button>
          </div>
        </form>
      </div>
    </Modal>;
}